import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Loader2, Search, Package, FolderOpen } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const ServiceCatalogTab = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [filterCat, setFilterCat] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddService, setShowAddService] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editService, setEditService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', categoryId: '', description: '', defaultDuration: '', defaultPrice: '' });
  const [catForm, setCatForm] = useState({ name: '', code: '' });

  const token = getFromLocalStorage('accessToken');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const loadData = async () => {
    try {
      const [catRes, svcRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/service-catalog/categories`, { headers }),
        fetch(`${BACKEND_URL}/api/service-catalog/services?activeOnly=false`, { headers }),
      ]);
      if (catRes.ok) { const d = await catRes.json(); setCategories(d.categories || []); }
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
    } catch (e) { console.error('Load catalog data error:', e); }
  };

  useEffect(() => { loadData(); }, []);

  const filteredServices = services.filter(s => {
    if (filterCat !== 'all' && s.categoryId !== filterCat) return false;
    if (search) {
      const term = search.toLowerCase();
      if (!s.name.toLowerCase().includes(term) && !s.code.toLowerCase().includes(term)) return false;
    }
    return true;
  });

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-catalog/categories`, {
        method: 'POST', headers, body: JSON.stringify(catForm),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Hata'); }
      toast({ title: "Kategori eklendi" });
      setCatForm({ name: '', code: '' });
      setShowAddCategory(false);
      loadData();
    } catch (err) { toast({ title: "Hata", description: err.message, variant: "destructive" }); }
    setLoading(false);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name, code: form.code, categoryId: form.categoryId,
        description: form.description,
        defaultDuration: form.defaultDuration ? parseFloat(form.defaultDuration) : null,
        defaultPrice: form.defaultPrice ? parseFloat(form.defaultPrice) : null,
      };
      const url = editService
        ? `${BACKEND_URL}/api/service-catalog/services/${editService.id}`
        : `${BACKEND_URL}/api/service-catalog/services`;
      const method = editService ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Hata'); }
      toast({ title: editService ? "Hizmet guncellendi" : "Hizmet eklendi" });
      setForm({ name: '', code: '', categoryId: '', description: '', defaultDuration: '', defaultPrice: '' });
      setShowAddService(false);
      setEditService(null);
      loadData();
    } catch (err) { toast({ title: "Hata", description: err.message, variant: "destructive" }); }
    setLoading(false);
  };

  const openEdit = (svc) => {
    setEditService(svc);
    setForm({
      name: svc.name, code: svc.code, categoryId: svc.categoryId,
      description: svc.description || '', defaultDuration: svc.defaultDuration || '',
      defaultPrice: svc.defaultPrice || '',
    });
    setShowAddService(true);
  };

  const catMap = {};
  categories.forEach(c => { catMap[c.id] = c.name; });

  return (
    <div className="space-y-4" data-testid="service-catalog-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-white font-semibold text-sm flex items-center gap-2">
          <Package className="h-4 w-4 text-brand" />Hizmet Katalogu ({services.length})
        </h2>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAddCategory(true)} className="bg-gray-700 hover:bg-gray-600 text-white text-xs h-7">
            <FolderOpen className="h-3 w-3 mr-1" />Kategori Ekle
          </Button>
          <Button size="sm" onClick={() => { setEditService(null); setForm({ name: '', code: '', categoryId: '', description: '', defaultDuration: '', defaultPrice: '' }); setShowAddService(true); }}
            className="bg-brand hover:bg-brand text-white text-xs h-7" data-testid="add-catalog-service-btn">
            <Plus className="h-3 w-3 mr-1" />Hizmet Ekle
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hizmet ara..."
            className="bg-gray-900 border-gray-700 text-white text-xs pl-7 h-8" />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
          className="bg-gray-900 border border-gray-700 text-white rounded text-xs p-1">
          <option value="all">Tum Kategoriler</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Service grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
        {filteredServices.map(s => (
          <Card key={s.id} className={`border ${s.isActive ? 'bg-gray-800/70 border-gray-700/40' : 'bg-gray-900/50 border-gray-600/30 opacity-60'}`}>
            <CardContent className="p-2.5 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-xs font-medium truncate">{s.name}</p>
                  {!s.isActive && <Badge className="bg-gray-600 text-[9px]">Pasif</Badge>}
                </div>
                <div className="flex gap-2 mt-0.5">
                  <span className="text-gray-500 text-[10px]">{s.code}</span>
                  <span className="text-brand/70 text-[10px]">{s.categoryName}</span>
                  {s.defaultDuration && <span className="text-gray-600 text-[10px]">{s.defaultDuration}s</span>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-6 w-6 p-0 text-gray-400 hover:text-white">
                <Edit className="h-3 w-3" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Service Dialog */}
      <Dialog open={showAddService} onOpenChange={setShowAddService}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader><DialogTitle className="text-white text-lg">{editService ? 'Hizmet Duzenle' : 'Yeni Hizmet'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveService} className="space-y-3">
            <div><Label className="text-gray-400 text-xs">Ad *</Label>
              <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} required
                className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
            <div><Label className="text-gray-400 text-xs">Kod *</Label>
              <Input value={form.code} onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} required
                className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
            <div><Label className="text-gray-400 text-xs">Kategori *</Label>
              <select value={form.categoryId} onChange={(e) => setForm(prev => ({ ...prev, categoryId: e.target.value }))} required
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm">
                <option value="">Sec...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-gray-400 text-xs">Varsayilan Sure (saat)</Label>
                <Input type="number" value={form.defaultDuration} onChange={(e) => setForm(prev => ({ ...prev, defaultDuration: e.target.value }))}
                  className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
              <div><Label className="text-gray-400 text-xs">Varsayilan Fiyat (TL)</Label>
                <Input type="number" value={form.defaultPrice} onChange={(e) => setForm(prev => ({ ...prev, defaultPrice: e.target.value }))}
                  className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setShowAddService(false)} className="flex-1 border-gray-600 text-gray-300 text-sm">Iptal</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-brand hover:bg-brand text-white text-sm">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (editService ? 'Guncelle' : 'Ekle')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white text-lg">Yeni Kategori</DialogTitle></DialogHeader>
          <form onSubmit={handleAddCategory} className="space-y-3">
            <div><Label className="text-gray-400 text-xs">Ad *</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm(prev => ({ ...prev, name: e.target.value }))} required
                className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
            <div><Label className="text-gray-400 text-xs">Kod *</Label>
              <Input value={catForm.code} onChange={(e) => setCatForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} required
                className="bg-gray-900 border-gray-700 text-white text-sm" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddCategory(false)} className="flex-1 border-gray-600 text-gray-300 text-sm">Iptal</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-gray-600 hover:bg-gray-700 text-white text-sm">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ekle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceCatalogTab;
